// src/layout/AppHeader.tsx
import React, { useState, useRef } from 'react';
import { Link as RouterLink, useLocation, useParams, useNavigate } from 'react-router-dom';
import {
    AppBar, Toolbar, Typography, Box, Button, IconButton, Popover,
    List, ListItemIcon, ListItemText, Grid, ListItemButton, Tooltip, Divider, useTheme
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import { menuConfig } from '../config/menuConfig';
import { toggleThemeMode } from '../app/slices/uiSlice';
import Logo from '../components/common/Logo'; // UPDATED: Import the new Logo component
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SearchBar from '../components/search/SearchBar';
import LanguageSwitcher from '../components/common/LanguageSwitcher';
import UserMenu from '../components/common/UserMenu';
import CreditsDisplay from '../components/common/CreditsDisplay'; // NEW: Import CreditsDisplay
import { ToolConfig } from '../types';

const AppHeader: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const params = useParams<{ category?: string }>();
    const navigate = useNavigate();
    const theme = useTheme(); // NEW: Get theme for sx props

    const { themeMode } = useAppSelector((state) => state.ui);
    const { isAuthenticated } = useAppSelector((state) => state.auth);

    // State to track which menu is currently active.
    const [activeMenu, setActiveMenu] = useState<string | null>(null);

    // Refs to store anchor elements and the close timer.
    const anchorRefs = useRef<{ [key: string]: HTMLElement }>({});
    const closeTimerRef = useRef<NodeJS.Timeout | null>(null);

    const handleOpen = (event: React.MouseEvent<HTMLElement>, menuId: string) => {
        // Clear any pending close timer.
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
        // Store the anchor element for the popover positioning.
        anchorRefs.current[menuId] = event.currentTarget;
        // Set this menu as the active one.
        setActiveMenu(menuId);
    };

    const handleClose = () => {
        // Set a timer to close the menu. This gives a grace period for the mouse to move.
        closeTimerRef.current = setTimeout(() => {
            setActiveMenu(null);
        }, 200); // 200ms grace period
    };

    const handleImmediateClose = () => {
        // Used for closing when an item is clicked.
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
        }
        setActiveMenu(null);
    }

    const currentCategoryConfig = menuConfig.find(cat => cat.id === params.category);
    const onCategoryPage = !!currentCategoryConfig;

    const renderPopoverContent = (menuId: string) => {
        let columns: { titleKey: string; tools: ToolConfig[] }[] = [];
        const isMainToolsPopover = menuId === 'main-tools';

        if (isMainToolsPopover) {
            if (onCategoryPage && currentCategoryConfig) {
                const uncategorizedTools = currentCategoryConfig.tools.filter(t => !t.subCategoryId);
                if (uncategorizedTools.length > 0) {
                    columns.push({ titleKey: currentCategoryConfig.nameKey, tools: uncategorizedTools });
                }
                currentCategoryConfig.subCategories?.forEach(sub => {
                    const toolsInSub = currentCategoryConfig.tools.filter(t => t.subCategoryId === sub.id)
                    if (toolsInSub.length > 0) {
                        columns.push({ titleKey: sub.nameKey, tools: toolsInSub });
                    }
                });

            } else {
                columns = menuConfig.map(category => ({
                    titleKey: category.nameKey,
                    tools: category.tools,
                }));
            }
        } else {
            const subCategory = currentCategoryConfig?.subCategories?.find(s => s.id === menuId);
            if (subCategory && currentCategoryConfig) {
                const toolsInSub = currentCategoryConfig.tools.filter(t => t.subCategoryId === subCategory.id);
                if(toolsInSub.length > 0) {
                    columns.push({ titleKey: subCategory.nameKey, tools: toolsInSub });
                }
            }
        }

        return (
            <Grid container flexWrap="nowrap" spacing={1} sx={{ p:1 }}>
                {columns.map((col) => (
                    <Grid item sx={{ minWidth: 180 }} key={col.titleKey} {...{} as any}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ p: '8px 16px', fontWeight: 600 }}>
                            {t(col.titleKey, { ns: 'tools' })}
                        </Typography>
                        <List dense>
                            {col.tools.map(tool => (
                                <Tooltip
                                    key={tool.id}
                                    title={t(tool.descKey, { ns: 'tools' })}
                                    placement="right"
                                    arrow
                                >
                                    <ListItemButton
                                        component={RouterLink}
                                        to={tool.route}
                                        onClick={handleImmediateClose}
                                        sx={{ borderRadius: 1, p: 1 }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                            <tool.icon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText primary={t(tool.nameKey, { ns: 'tools' })} />
                                    </ListItemButton>
                                </Tooltip>
                            ))}
                        </List>
                    </Grid>
                ))}
            </Grid>
        );
    };

    const renderDynamicMenu = () => {
        return (
            <>
                {/* === Main Tools Menu Container === */}
                <Box onMouseEnter={(e) => handleOpen(e, 'main-tools')} onMouseLeave={handleClose}>
                    <Button
                        color="inherit"
                        endIcon={<ArrowDropDownIcon />}
                        onClick={() => onCategoryPage && currentCategoryConfig && navigate(currentCategoryConfig.route)}
                        aria-haspopup="true"
                    >
                        {onCategoryPage && currentCategoryConfig ? t(currentCategoryConfig.nameKey, { ns: 'tools' }) : t('all_tools', { ns: 'common' })}
                    </Button>
                    <Popover
                        open={activeMenu === 'main-tools'}
                        anchorEl={anchorRefs.current['main-tools']}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        PaperProps={{
                            sx: {
                                mt: 1,
                                boxShadow: 3,
                                borderRadius: 2,
                                maxWidth: 'calc(100vw - 32px)',
                                overflowX: 'auto',
                            },
                        }}
                        sx={{ pointerEvents: 'none' }}
                        slotProps={{ paper: { sx: { pointerEvents: 'auto' } } }}
                        disableRestoreFocus
                    >
                        {renderPopoverContent('main-tools')}
                    </Popover>
                </Box>

                {onCategoryPage && currentCategoryConfig && <Divider orientation="vertical" flexItem sx={{ mx: 1, my: 1.5 }} />}

                {/* Dynamic menu items for tools without sub-categories */}
                {onCategoryPage && currentCategoryConfig?.tools.filter(t => t.showInHeader && !t.subCategoryId).map(tool => (
                    <Button key={tool.id} component={RouterLink} to={tool.route} color="inherit">
                        {t(tool.nameKey, { ns: 'tools' })}
                    </Button>
                ))}

                {/* === Dynamic Sub-Category Menu Containers === */}
                {onCategoryPage && currentCategoryConfig?.subCategories?.map(sub => (
                    <Box key={sub.id} onMouseEnter={(e) => handleOpen(e, sub.id)} onMouseLeave={handleClose}>
                        <Button
                            color="inherit"
                            endIcon={<ArrowDropDownIcon />}
                            aria-haspopup="true"
                        >
                            {t(sub.nameKey, { ns: 'tools' })}
                        </Button>
                        <Popover
                            open={activeMenu === sub.id}
                            anchorEl={anchorRefs.current[sub.id]}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                            PaperProps={{
                                sx: { mt: 1, boxShadow: 3, borderRadius: 2 },
                            }}
                            sx={{ pointerEvents: 'none' }}
                            slotProps={{ paper: { sx: { pointerEvents: 'auto' } } }}
                            disableRestoreFocus
                        >
                            {renderPopoverContent(sub.id)}
                        </Popover>
                    </Box>
                ))}
            </>
        );
    };

    return (
        <AppBar
            position="fixed"
            elevation={0}
            sx={{
                bgcolor: 'background.paper',
                color: 'text.primary',
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                boxShadow: (theme) => `0 2px 4px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.1)'}`,
            }}
        >
            <Toolbar>
                <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                    <Logo height={36} />
                    <Box sx={{ ml: 6, display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
                        {renderDynamicMenu()}
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 2.5 } }}>
                    {/* NEW: Show search bar on all devices */}
                    <SearchBar />
                    {/* NEW: On desktop, show credits, language, theme toggles, and login/signup buttons */}
                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 2.5 }}>
                        {isAuthenticated && <CreditsDisplay />}
                        <LanguageSwitcher />
                        <Tooltip title={t(themeMode === 'dark' ? "switch_to_light" : "switch_to_dark", { ns: 'common' })}>
                            <IconButton onClick={() => dispatch(toggleThemeMode())} color="inherit" aria-label="toggle theme">
                                {themeMode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                            </IconButton>
                        </Tooltip>
                        {!isAuthenticated && (
                            <>
                                <Button component={RouterLink} to="/login" color="inherit">
                                    {t('login', { ns: 'common' })}
                                </Button>
                                <Button
                                    component={RouterLink}
                                    to="/signup"
                                    variant="contained"
                                    disableElevation
                                    sx={{
                                        bgcolor: 'inverse.main',
                                        color: 'inverse.contrastText',
                                        '&:hover': {
                                            bgcolor: 'inverse.hover.main',
                                        },
                                    }}
                                >
                                    {t('signup', { ns: 'common' })}
                                </Button>
                            </>
                        )}
                    </Box>

                    {/* This menu now handles mobile options and user state */}
                    <UserMenu />
                </Box>
            </Toolbar>
        </AppBar>
    );
};

export default AppHeader;